// src/services/dataroomService.js (SAFE VERSION - Use this first)
import { supabase } from '../config';

const dataroomService = {
  // ... (existing functions retained)

  // ===========================================
  // NEW: Bookmark Functions
  // ===========================================
  async createBookmark(userId, projectId, itemId, itemName, notes = null) {
    const { data, error } = await supabase
      .from('dataroom_bookmarks')
      .insert({
        user_id: userId,
        project_id: projectId,
        dataroom_item_id: itemId,
        name: itemName,
        notes: notes,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async fetchBookmarks(userId, projectId) {
    // Fetch bookmarks for a specific user and project, joining item details
    const { data, error } = await supabase
      .from('dataroom_bookmarks')
      .select(`
        *,
        dataroom_items(name, is_folder, file_type, file_path),
        dataroom_projects(project_name)
      `)
      .eq('user_id', userId)
      .eq('project_id', projectId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  async fetchAllBookmarks(userId) {
    // Fetch all bookmarks for a user across all projects
    const { data, error } = await supabase
      .from('dataroom_bookmarks')
      .select(`
        *,
        dataroom_items(name, is_folder, file_type, file_path),
        dataroom_projects(project_name)
      `)
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  async deleteBookmark(bookmarkId) {
    const { error } = await supabase
      .from('dataroom_bookmarks')
      .update({ is_active: false }) // Soft delete
      .eq('id', bookmarkId);

    if (error) throw error;
  },
  
  // ... (rest of the file retained, including moveItemHierarchy)
  getAllDescendants: (itemId, allItems) => {
    const descendants = [];
    const children = allItems.filter(item => item.parent_id === itemId);
    
    for (const child of children) {
      descendants.push(child.id);
      descendants.push(...dataroomService.getAllDescendants(child.id, allItems));
    }
    return descendants;
  },

  async moveItemHierarchy(itemId, newParentId, allItems) {
    const item = allItems.find(i => i.id === itemId);
    if (!item) {
      throw new Error('Item not found for move operation.');
    }
    
    // Check if moving to its own descendant (prevent recursive loop)
    if (item.is_folder && newParentId !== null) {
        const descendants = dataroomService.getAllDescendants(itemId, allItems);
        if (descendants.includes(newParentId)) {
            throw new Error('Cannot move a folder into its own subfolder.');
        }
    }
    
    const { error: moveError } = await supabase
        .from('dataroom_items')
        .update({ parent_id: newParentId })
        .eq('id', itemId);
        
    if (moveError) {
        throw moveError;
    }
    
    return { success: true, movedItemId: itemId };
  },
  
  async reorderItems(projectId, fromIndex, toIndex, parentId) {
    console.log('Reorder stub:', { projectId, fromIndex, toIndex, parentId });
    // TODO: Implement reordering logic if needed
  },

  async moveItem(itemId, newParentId) {
    const { error } = await supabase
      .from('dataroom_items')
      .update({ parent_id: newParentId })
      .eq('id', itemId);
    if (error) throw error;
  },

  async createTemplateFolders(projectId, parentId = null, folders = [], userId) {
    const insertedFolders = [];
    
    for (const folderName of folders) {
      const { data, error } = await supabase
        .from('dataroom_items')
        .insert({
          project_id: projectId,
          parent_id: parentId,
          name: folderName,
          type: 'folder',
          is_folder: true,
          created_by: userId
        })
        .select()
        .single();
        
      if (error) {
        console.error('Error creating template folder:', error);
        throw error;
      }
      
      insertedFolders.push(data);
    }
    
    return insertedFolders;
  },

  async createFolder(projectId, parentId, name, userId) {
    const { data, error } = await supabase
      .from('dataroom_items')
      .insert({
        project_id: projectId,
        parent_id: parentId,
        name: name,
        type: 'folder',
        is_folder: true,
        created_by: userId
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async uploadFile(projectId, parentId, name, filePath, fileSize, fileType, userId) {
    const { data, error } = await supabase
      .from('dataroom_items')
      .insert({
        project_id: projectId,
        parent_id: parentId,
        name: name,
        type: 'file',
        is_folder: false,
        file_path: filePath,
        file_size: fileSize,
        file_type: fileType,
        created_by: userId
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteItemSafe(itemId, isEmbedded = false) {
    if (isEmbedded) {
      throw new Error('Cannot delete embedded content. Remove the embed or delete from the original dataroom.');
    }
    
    return this.deleteItem(itemId);
  },

  async deleteItem(itemId) {
    const { error } = await supabase
      .from('dataroom_items')
      .delete()
      .eq('id', itemId);
      
    if (error) throw error;
  },

  async renameItemSafe(itemId, newName, isEmbedded = false) {
    if (isEmbedded) {
      throw new Error('Cannot rename embedded content. Rename in the original dataroom.');
    }
    
    return this.renameItem(itemId, newName);
  },

  async renameItem(itemId, newName) {
    const { error } = await supabase
      .from('dataroom_items')
      .update({ name: newName })
      .eq('id', itemId);
      
    if (error) throw error;
  },

  buildTreeWithEmbeds(items, parentId = null) {
    return this.buildTree(items, parentId);
  },

  buildTree(items, parentId = null) {
    return items
      .filter(item => item.parent_id === parentId)
      .map(item => ({
        ...item,
        children: this.buildTree(items, item.id)
      }));
  },

  isEmbeddedItem(item) {
    return item.isVirtual || item.is_embedded_folder;
  },

  getSourceProject(item) {
    if (item.isVirtual) {
      return item.originalProjectId;
    }
    if (item.is_embedded_folder) {
      return item.embedded_project_id;
    }
    return null;
  }
};

export default dataroomService;